import { Component, ViewChild, inject } from '@angular/core';
import { ConfirmationService, MessageService, PrimeIcons } from 'primeng/api';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  CompositionControllerService,
  CompositionDetails
} from 'src/app/admin-api';
import {
  TableActionButton,
  TableColumn,
  TableComponent
} from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { prepareComposition } from '../editions-dashboard.component';

@Component({
  selector: 'app-pending-preparation',
  templateUrl: './pending-preparation.component.html',
  styleUrl: './pending-preparation.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class PendingPreparationComponent {
  @ViewChild('pendingTable')
  pendingTable: TableComponent | undefined;

  private compositionService = inject(CompositionControllerService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private config = inject(DynamicDialogConfig);

  pending: CompositionDetails[] = this.config.data.pending;
  actionButtons: Array<TableActionButton> = [
    new TableActionButton(
      '',
      'prepare',
      PrimeIcons.PLAY,
      (item: CompositionDetails) => item.pendingPreparation > 0,
      'p-button-success',
      'Preparar composição',
      'bottom'
    ),
    new TableActionButton(
      '',
      'delete',
      PrimeIcons.TRASH,
      (item: CompositionDetails) => item.pendingPreparation > 0,
      '',
      'Excluir composição',
      'bottom',
      true,
      true,
      'danger'
    )
  ];
  cols = [
    new TableColumn.Builder()
      .setHeader('')
      .setField('')
      .setType('checkbox')
      .setSelectDisabledTooltip('Composição já preparada')
      .build(),
    new TableColumn.Builder()
      .setHeader('Id')
      .setField('compositionId')
      .setRouterLink('/operations/compositions/id/')
      .setRouterLinkFieldName('compositionId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Composição')
      .setField('compositionName')
      .setRouterLink('/operations/compositions/id/')
      .setRouterLinkFieldName('compositionId')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Pendente preparo')
      .setField('pendingPreparation')
      .setFilter(false)
      .setType('formattedInteger')
      .build(),
    new TableColumn.Builder().setHeader('Ação').setType('button').build()
  ];

  rowSelectable = (item: { data: CompositionDetails }): boolean =>
    item.data.pendingPreparation > 0;

  get selectedCount(): number {
    return this.pendingTable?.selectedItems?.length || 0;
  }

  callAction($event: {
    item: CompositionDetails;
    $event: Event;
    action: string;
  }): void {
    if ($event.action === 'prepare') {
      this.prepareComposition($event.item);
    } else if ($event.action === 'delete') {
      this.confirmDeleteComposition($event);
    }
  }

  async prepareComposition(composition: CompositionDetails) {
    try {
      LoaderService.showLoader();
      const result = await prepareComposition(
        composition,
        this.compositionService
      );
      composition.pendingPreparation = result.composition.pending;
      this.messageService.add(result.message);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  confirmDeleteComposition($event: {
    item: CompositionDetails;
    $event: Event;
    action: string;
  }): void {
    this.confirmationService.confirm({
      acceptButtonStyleClass: 'p-button-danger',
      acceptIcon: PrimeIcons.TRASH,
      acceptLabel: 'Excluir',
      rejectButtonStyleClass: 'p-button-primary',
      rejectIcon: PrimeIcons.ARROW_LEFT,
      rejectLabel: 'Voltar',
      target: $event.$event.target,
      message: `Tem certeza que deseja excluir a composição '${$event.item.compositionName}'?`,
      header: 'Excluir composição',
      accept: async () => {
        await this.deleteComposition($event.item);
      }
    });
  }

  async deleteComposition(composition: CompositionDetails) {
    LoaderService.showLoader();
    try {
      const detail = await lastValueFrom(
        this.compositionService
          .deleteComposition(composition.compositionId)
          .pipe(map((data) => data.result))
      );
      this.pending = this.pending.filter(
        (item) => item.compositionId !== composition.compositionId
      );
      this.messageService.add({
        detail,
        summary: 'Sucesso',
        severity: 'success'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  confirmPrepareSelected(): void {
    const selected = this.getSelectedCompositions();
    if (!selected.length) return;
    this.confirmationService.confirm({
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      message: `Deseja preparar ${selected.length} composição(ões) selecionada(s)?`,
      header: 'Preparar composições',
      accept: async () => {
        await this.prepareSelectedCompositions(selected);
      }
    });
  }

  confirmDeleteSelected(): void {
    const selected = this.getSelectedCompositions();
    if (!selected.length) return;
    this.confirmationService.confirm({
      acceptButtonStyleClass: 'p-button-danger',
      acceptIcon: PrimeIcons.TRASH,
      acceptLabel: 'Excluir',
      rejectButtonStyleClass: 'p-button-primary',
      rejectIcon: PrimeIcons.ARROW_LEFT,
      rejectLabel: 'Voltar',
      message: `Tem certeza que deseja excluir ${selected.length} composição(ões) selecionada(s)?`,
      header: 'Excluir composições',
      accept: async () => {
        await this.deleteSelectedCompositions(selected);
      }
    });
  }

  async prepareSelectedCompositions(compositions: CompositionDetails[]) {
    LoaderService.showLoader();
    try {
      const results = { success: 0, partial: 0, error: 0 };
      for (const composition of compositions) {
        try {
          const result = await prepareComposition(
            composition,
            this.compositionService
          );
          composition.pendingPreparation = result.composition.pending;
          if (result.message.severity === 'success') results.success++;
          else if (result.message.severity === 'warn') results.partial++;
          else results.error++;
        } catch {
          results.error++;
        }
      }
      const details: string[] = [];
      if (results.success) {
        details.push(`${results.success} preparada(s) com sucesso`);
      }
      if (results.partial) {
        details.push(`${results.partial} preparada(s) parcialmente`);
      }
      if (results.error) {
        details.push(`${results.error} com erro`);
      }
      this.messageService.add({
        severity:
          results.error && !results.success && !results.partial
            ? 'error'
            : results.partial || results.error
              ? 'warn'
              : 'success',
        summary: 'Preparo em lote',
        detail: details.join('; ')
      });
      this.clearTableSelection();
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async deleteSelectedCompositions(compositions: CompositionDetails[]) {
    LoaderService.showLoader();
    try {
      let successCount = 0;
      let errorCount = 0;
      const deletedIds = new Set<number>();
      for (const composition of compositions) {
        try {
          await lastValueFrom(
            this.compositionService.deleteComposition(composition.compositionId)
          );
          deletedIds.add(composition.compositionId);
          successCount++;
        } catch {
          errorCount++;
        }
      }
      this.pending = this.pending.filter(
        (item) => !deletedIds.has(item.compositionId)
      );
      this.messageService.add({
        severity: errorCount && !successCount ? 'error' : 'success',
        summary: 'Exclusão em lote',
        detail: `${successCount} excluída(s)${
          errorCount ? `; ${errorCount} com erro` : ''
        }.`
      });
      this.clearTableSelection();
    } finally {
      LoaderService.showLoader(false);
    }
  }

  getSelectedCompositions(): CompositionDetails[] {
    return (
      (this.pendingTable?.selectedItems as CompositionDetails[]) || []
    );
  }

  clearTableSelection(): void {
    if (!this.pendingTable) return;
    this.pendingTable.selectedItems = [];
    this.pendingTable.checkboxAll = false;
  }
}
